'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import memoWithDisplayName from '../utils/memoWithDisplayName';
import validHref from '../utils/propTypes/validHref';
import { throwErrorAsync } from '../utils/ThrowError';
import { isUnsafeUrl } from '../utils/UnsafeUrl';
const SHAPE_CLASSES = {
  default: '',
  circle: 'img-circle private-image--circle',
  rounded: 'img-rounded private-image--rounded',
  thumbnail: 'img-thumbnail private-image--thumbnail'
};
const OBJECT_FIT_CLASSES = {
  fill: '',
  contain: 'private-image--object-fit-contain'
};
const UIImage = memoWithDisplayName('UIImage', /*#__PURE__*/forwardRef((props, ref) => {
  const {
    bordered,
    className,
    objectFit,
    onError,
    onLoad,
    responsive,
    shape,
    src,
    style
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["bordered", "className", "objectFit", "onError", "onLoad", "responsive", "shape", "src", "style"]);

  if (isUnsafeUrl(src)) {
    throwErrorAsync(new Error(`UIImage: Detected XSS attempt! src="${src}"`));
  }

  const [loadFailed, setLoadFailed] = useState(false);
  const [styleOverrides, setStyleOverrides] = useState(null);
  /** A ref to the <img> element */

  const imgRef = useRef(null);
  const img = imgRef.current;
  const needsPolyfill = !('objectFit' in document.documentElement.style || objectFit !== 'contain');

  const polyfillObjectFit = () => {
    // If style overrides already exist, do nothing
    if (styleOverrides && Object.keys(styleOverrides).length !== 0) return; // Compute the natural image ratio

    const {
      naturalWidth,
      naturalHeight
    } = img;
    if (!naturalWidth || !naturalHeight) return;
    const imgAspectRatio = naturalWidth / naturalHeight;
    const renderedWidth = img.clientWidth;
    const renderedHeight = img.clientHeight;
    const fittedHeight = renderedWidth / imgAspectRatio;
    const fittedWidth = renderedHeight * imgAspectRatio;

    if (renderedHeight > fittedHeight) {
      setStyleOverrides({
        height: fittedHeight
      });
    } else if (renderedWidth > fittedWidth) {
      setStyleOverrides({
        width: fittedWidth
      });
    }
  };
  /** Re-render without any style overrides, then recompute style overrides */


  const resetOverrides = () => {
    setStyleOverrides({});
  };

  useLayoutEffect(() => {
    if (!needsPolyfill || !img) return undefined;
    polyfillObjectFit();
    addEventListener('resize', resetOverrides);
    return () => {
      removeEventListener('resize', resetOverrides);
    };
  });

  const handleImageError = evt => {
    if (onError) onError(evt);
    setLoadFailed(true);
  };

  const handleImageLoad = evt => {
    if (onLoad) onLoad(evt);
    imgRef.current = evt.target;
    setLoadFailed(false);
    if (needsPolyfill) resetOverrides();
  };
  /* eslint-disable jsx-a11y/alt-text */


  return /*#__PURE__*/_jsx("img", Object.assign({}, rest, {
    className: classNames('private-image', SHAPE_CLASSES[shape], OBJECT_FIT_CLASSES[objectFit], className, responsive && 'img-responsive private-image--responsive', loadFailed && 'image-load-failed', bordered && 'private-image--bordered'),
    onError: handleImageError,
    onLoad: handleImageLoad,
    ref: ref,
    src: isUnsafeUrl(src) ? null : src,
    style: Object.assign({}, style, {}, styleOverrides)
  }));
  /* eslint-enable jsx-a11y/alt-text */
}));
UIImage.propTypes = {
  alt: PropTypes.string.isRequired,
  bordered: PropTypes.bool.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onError: PropTypes.func,
  onLoad: PropTypes.func,
  objectFit: PropTypes.oneOf(Object.keys(OBJECT_FIT_CLASSES)).isRequired,
  responsive: PropTypes.bool.isRequired,
  shape: PropTypes.oneOf(Object.keys(SHAPE_CLASSES)).isRequired,
  src: validHref.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
UIImage.defaultProps = {
  alt: '',
  bordered: false,
  objectFit: 'fill',
  responsive: true,
  shape: 'default'
};
export default UIImage;